<template>
  <div :style="{ marginTop: '20px', paddingBottom: '80px' }">

    <div v-if="!loading" style="text-align: left; color: #666;" class="d-flex align-items-center justify-content-center">
      <form @submit.prevent="save_config">
        <table width="90%" style="font-size: 18px; font-weight: bold" cellpadding="10px">
          <tr v-if="isEffectiveAdmin" data-field="tenant_id">
            <td align="right" width="500">Tenant ID</td>
            <td width="800">
              <div class="d-flex align-items-center">
                <b-form-input :value="user.tenant" type="text" readonly class="flex-grow-1"></b-form-input>
                <b-button
                  variant="link"
                  class="p-0 ml-2 flex-shrink-0"
                  @click="copy_tenant_id"
                  :title="tenant_id_copied ? 'Copied!' : 'Copy tenant ID'"
                  aria-label="Copy tenant ID"
                >
                  <i :class="tenant_id_copied ? 'fa fa-check' : 'fa fa-copy'" aria-hidden="true"></i>
                </b-button>
              </div>
            </td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr data-field="tenant_name">
            <td align="right" width="500">Name</td>
            <td width="800"><b-form-input v-model="model.tenant_name" type="text"></b-form-input></td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr data-field="timezone">
            <td align="right" width="500">Timezone</td>
            <td width="800"><b-form-input v-model="model.timezone" type="text" :readonly="!isEffectiveAdmin"></b-form-input></td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr data-field="company_id">
            <td align="right" width="500">Company ID</td>
            <td width="800"><b-form-input v-model="model.company_id" type="text" :readonly="!isEffectiveAdmin"></b-form-input></td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr data-field="motorcoach_organizations">
            <td align="right" width="500">Motorcoach Organizations</td>
            <td width="800">
              <b-form-checkbox-group
                v-model="model.motorcoach_organizations"
                :options="motorcoach_organization_options"
                class="motorcoach-org-checkboxes"
              ></b-form-checkbox-group>
            </td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr data-field="aws_phone_number">
            <td align="right" width="500">Phone Number</td>
            <td width="800"><b-form-input v-model="model.aws_phone_number" type="text" :readonly="!isEffectiveAdmin"></b-form-input></td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr>
            <td align="right" width="500">Vehicle Yards</td>
            <td width="800"><b-button variant="" @click="manage_sos_yards = !manage_sos_yards">Manage Yards</b-button></td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr data-field="samsara_tag_filter">
            <td align="right" width="500">Vehicle Tag Filter</td>
            <td width="800">
              <!-- Backend does a tag-set intersection (util/samsara.py) — plain allowlist
                   of tag names, no `*`/`-` wildcard support. -->
              <FilterSettingEditor
                v-model="model.samsara_tag_filter"
                fieldKey="samsara_tag_filter"
                variant="list"
                :allow-raw-edit="isEffectiveAdmin"
                @validation="onFilterValidation"
              />
            </td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr data-field="samsara_driver_tag_filter">
            <td align="right" width="500">Driver Tag Filter</td>
            <td width="800">
              <!-- Same tag-set intersection allowlist behavior as the vehicle tag filter above. -->
              <FilterSettingEditor
                v-model="model.samsara_driver_tag_filter"
                fieldKey="samsara_driver_tag_filter"
                variant="list"
                :allow-raw-edit="isEffectiveAdmin"
                @validation="onFilterValidation"
              />
            </td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr data-field="email_sender_prefix">
            <td align="right" width="500">Email Sender Prefix</td>
            <td width="800"><b-form-input v-model="model.email_sender_prefix" type="text"></b-form-input></td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr data-field="email_response_forward_address">
            <td align="right" width="500">Email Response Forward Address</td>
            <td width="800"><b-form-input v-model="model.email_response_forward_address" type="text"></b-form-input></td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr data-field="email_response_forward_filters">
            <td align="right" width="500">Email Response Forward Filters</td>
            <td width="800">
              <!-- Backend skips forwarding when the incoming message exactly matches
                   (lowercased) one of these comma-split tokens — plain exclusion list,
                   no `*`/`-` wildcard support. -->
              <FilterSettingEditor
                v-model="model.email_response_forward_filters"
                fieldKey="email_response_forward_filters"
                variant="list"
                :allow-raw-edit="isEffectiveAdmin"
                @validation="onFilterValidation"
              />
            </td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr data-field="sms_response_email_recipients">
            <td align="right" width="500">SMS Response Email Recipients</td>
            <td width="800"><b-form-input v-model="model.sms_response_email_recipients" type="text"></b-form-input></td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr data-field="sms_response_text">
            <td align="right" width="500">SMS Response Text</td>
            <td width="800"><b-form-input v-model="model.sms_response_text" type="text"></b-form-input></td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr data-field="support_ticket_approver_emails">
            <td align="right" width="500">Support Ticket Approvers (CC'd on all tickets)</td>
            <td width="800"><b-form-input v-model="model.support_ticket_approver_emails" type="text"></b-form-input></td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr data-field="settings_change_email_enabled">
            <td align="right" width="500">Email Admins on Settings Change</td>
            <td width="800">
              <b-form-select v-model="model.settings_change_email_enabled" :options="boolean_options" style="width:100px" @change="on_settings_change_email_toggle"></b-form-select>
              <small class="text-muted d-block">Sends an email summarizing what changed whenever a tenant setting is updated.</small>
            </td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr v-if="model.settings_change_email_enabled === 'True'" data-field="settings_change_email_recipients">
            <td align="right" width="500">Settings Change Email Recipients</td>
            <td width="800">
              <b-form-input v-model="model.settings_change_email_recipients" type="text"></b-form-input>
              <small class="text-muted d-block">Comma-separated emails. Everyone on this list is emailed when any setting is changed.</small>
            </td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr data-field="help_center_enabled">
            <td align="right" width="500">Help Center Enabled</td>
            <td width="800"><b-form-select v-model="model.help_center_enabled" :options="boolean_options" style="width:100px"></b-form-select></td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr data-field="feedback_audit_mode_enabled">
            <td align="right" width="500">Feedback Audit Mode Enabled</td>
            <td width="800">
              <b-form-select v-model="model.feedback_audit_mode_enabled" :options="boolean_options" style="width:100px"></b-form-select>
              <small class="text-muted d-block">Show thumbs up/down and filter chips on Actuals and Alert History feedback; when off, only the flag/concern icon shows.</small>
            </td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr v-if="isEffectiveAdmin && telematics_type === 'SAUCON'">
            <td align="right" width="500">Saucon Beta Mode</td>
            <td width="800"><b-form-select v-model="model.saucon_beta_mode" :options="boolean_options" style="width:100px"></b-form-select></td>
            <td width="250">&nbsp;</td>
          </tr>
          <tr v-if="isEffectiveAdmin">
            <td align="right"><span style="color: var(--fi-rail-accent); font-weight: bold; font-size: 24px;" v-on:click="toggle_div(toggle,'isAdvVisible')">{{ toggle.isAdvVisible ? "▾" : "▸" }} Advanced</span></td>
            <td></td>
            <td></td>
          </tr>
          <template v-if="toggle.isAdvVisible">
            <tr data-field="active">
              <td align="right" width="500">Active</td>
              <td width="800"><b-form-select v-model="model.active" :options="boolean_options" style="width:100px"></b-form-select></td>
              <td width="250">&nbsp;</td>
            </tr>
            <!-- FI-2070: coaching portal rollout flag. lives under Advanced
                 (admin-only) while the portal is still Initek-gated — the
                 nav registry and router guard both read it, and staff see
                 the section regardless via the admin bypass. -->
            <tr data-field="coaching_portal_enabled">
              <td align="right" width="500">Coaching Portal Enabled</td>
              <td width="800">
                <b-form-select v-model="model.coaching_portal_enabled" :options="boolean_options" style="width:100px"></b-form-select>
                <small class="text-muted d-block">Driver coaching tickets, board, flagged drivers and recognition. Requires Driver Manager for the flag feed.</small>
              </td>
              <td width="250">&nbsp;</td>
            </tr>
            <tr data-field="org_id">
              <td align="right" width="500">Org ID</td>
              <td width="800"><b-form-input v-model="model.org_id" type="text"></b-form-input></td>
              <td width="250">&nbsp;</td>
            </tr>
            <tr data-field="group_id">
              <td align="right" width="500">Group ID</td>
              <td width="800"><b-form-input v-model="model.group_id" type="text"></b-form-input></td>
              <td width="250">&nbsp;</td>
            </tr>
            <tr data-field="api_key">
              <td align="right" width="500">API Key</td>
              <!-- fullstory: fs-exclude so the integration api key is never captured -->
              <td width="800"><b-form-input v-model="model.api_key" type="text" class="fs-exclude"></b-form-input></td>
              <td width="250">&nbsp;</td>
            </tr>
            <tr v-if="tenant_type == 'TBN'" data-field="telematics_yard_overrides">
              <td align="right" width="500">Telematics Yard Overrides</td>
              <td width="800">
                <JsonObjectListEditor v-model="model.telematics_yard_overrides" fieldKey="telematics_yard_overrides" @validation="onJsonValidation" :allow-raw-json="isEffectiveAdmin" :columns="telematics_yard_overrides_columns"/>
                <b-form-invalid-feedback :state="jsonState('telematics_yard_overrides')">{{ jsonFeedback('telematics_yard_overrides') }}</b-form-invalid-feedback>
                <small class="text-muted d-block">Use a different telematics account for specific yards (falls back to the provider/credentials above for any yard not listed)</small>
              </td>
              <td width="250">&nbsp;</td>
            </tr>
            <tr v-if="tenant_type == 'TBN'" data-field="tbn_company_name">
              <td align="right" width="500">TBN Company Name</td>
              <td width="800"><b-form-input v-model="model.tbn_company_name" type="text"></b-form-input></td>
              <td width="250">&nbsp;</td>
            </tr>
            <tr v-if="tenant_type == 'TBN'" data-field="tbn_company_id">
              <td align="right" width="500">TBN Company ID</td>
              <td width="800"><b-form-input v-model="model.tbn_company_id" type="text"></b-form-input></td>
              <td width="250">&nbsp;</td>
            </tr>
            <tr v-if="tenant_type == 'TBN'" data-field="tbn_enterprise_id">
              <td align="right" width="500">TBN Enterprise ID</td>
              <td width="800"><b-form-input v-model="model.tbn_enterprise_id" type="text"></b-form-input></td>
              <td width="250">&nbsp;</td>
            </tr>
            <tr v-if="isDevEnvironment()" data-field="allowed_email_recipients">
              <td align="right" width="500">Allowed Email Receipients</td>
              <td width="800">
                <!-- Dev-only safety allowlist (coach_manager/emailer.py is_allowed_email) —
                     exact comma-split match, no `*`/`-` wildcard support. -->
                <FilterSettingEditor
                  v-model="model.allowed_email_recipients"
                  fieldKey="allowed_email_recipients"
                  variant="list"
                  :allow-raw-edit="isEffectiveAdmin"
                  @validation="onFilterValidation"
                />
              </td>
              <td width="250">&nbsp;</td>
            </tr>
            <tr v-if="isDevEnvironment()" data-field="allowed_sms_recipients">
              <td align="right" width="500">Allowed SMS Receipients</td>
              <td width="800">
                <!-- Same dev-only safety allowlist pattern as allowed_email_recipients. -->
                <FilterSettingEditor
                  v-model="model.allowed_sms_recipients"
                  fieldKey="allowed_sms_recipients"
                  variant="list"
                  :allow-raw-edit="isEffectiveAdmin"
                  @validation="onFilterValidation"
                />
              </td>
              <td width="250">&nbsp;</td>
            </tr>
            <tr v-if="isDevEnvironment()" data-field="process_booking_ids">
              <td align="right" width="500">Allowed Booking Ids</td>
              <td width="800">
                <!-- Dev-only safety allowlist (handle_booking.py is_booking_in_filter) —
                     comma-split exact-or-substring match, no `*`/`-` wildcard support. -->
                <FilterSettingEditor
                  v-model="model.process_booking_ids"
                  fieldKey="process_booking_ids"
                  variant="list"
                  :allow-raw-edit="isEffectiveAdmin"
                  @validation="onFilterValidation"
                />
              </td>
              <td width="250">&nbsp;</td>
            </tr>
          </template>
          </table>


      </form>
    </div>
    <div v-else>
      <br/><br/>
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

  <b-modal title="Manage Vehicle Yards" size="xl" v-model="manage_sos_yards" ok-only>
    <b>Found {{yardsLength}} Yards - these are used to support the BUS SOS and Vehicle Status Management features.</b><br/><br/>
    <div v-if="loading">
      <b-spinner style="width: 1rem; height: 1rem;" label="Loading..."></b-spinner>
    </div>
    <div v-else>
      <b-table v-if="model.vehicle_yards" :items="model.vehicle_yards" :fields="['yard_name','address','contact_name','phone','email','timezone','included_bases','actions']">
        <template slot="address" slot-scope="row">
          <a target='_blank' :href="'https://www.google.com/maps/search/?api=1&query=' + row.item.lat + ',' + row.item.lon">{{row.item.address}}</a>
          <br/>[<a target="_blank" :href="'./#/fleet-track/' + user.tenant + '/yard/' + row.item.yard_name.replaceAll(' ','+')">show yard map</a>]
        </template>
        <template slot="actions" slot-scope="row">
          <b-button size="sm" variant="outline-primary" class="mr-1" title="edit" aria-label="edit yard" @click="edit_yard(row.item)"><i class="fa fa-pencil"></i></b-button>
          <b-button size="sm" title="remove" aria-label="remove yard" @click="delete_yard(row.item.yard_name)"><i class="fa fa-trash"></i></b-button>
        </template>
      </b-table>
      <hr/>
      <div style="font-weight: bold; font-size: 18px;" v-on:click="toggle_div(toggle,'isAddYardVisible')">{{ editing_yard_name ? 'Edit Yard' : '+ Add New Yard' }}</div><br/>
       <span :style="toggle.isAddYardVisible ? '' : 'display:none'">
          <div>
            Yard Name: <b-form-input v-model="add_yard_model.yard_name" required/>
            Address: <b-form-input v-model="add_yard_model.address" required/>
            Contact Name: <b-form-input v-model="add_yard_model.contact_name" required/>
            Phone: <b-form-input v-model="add_yard_model.phone" required/>
            Email: <b-form-input v-model="add_yard_model.email" required/>
            Timezone: <b-form-input v-model="add_yard_model.timezone" required/>
            Included Bases: <b-form-input v-model="add_yard_model.included_bases"/>
            <small class="text-muted d-block">Comma-separated booking bases to also show in this yard's VSM views (e.g. Sar SB under Saratoga)</small>
          </div><br/>
          <b-button v-if="!loading" variant="success" size="sm" @click="save_yard()">{{ editing_yard_name ? 'Update Yard' : 'Save Yard' }}</b-button>
          <b-button v-if="editing_yard_name && !loading" variant="secondary" size="sm" class="ml-1" @click="cancel_edit_yard()">Cancel</b-button>
        </span>
     </div>
  </b-modal>

  <div class="tc-sticky-save">
    <transition name="tc-save-slide">
    <div v-if="isDirty || saving" class="d-flex align-items-center justify-content-center">
      <span v-if="isDirty && !saving" class="tc-unsaved-label mr-3" @click="scrollToFirstDirtyField()" title="Click to scroll to unsaved field" style="cursor:pointer">&#9679; Unsaved changes</span>
      <b-btn size="lg" variant="success" @click="save_config" :disabled="saving">
          <b-spinner v-if="saving" small class="mr-1"></b-spinner>
          {{ saving ? 'Saving...' : 'Save' }}
        </b-btn>
      &nbsp;&nbsp;
      <b-btn size="lg" variant="secondary" @click="load_data(true)">Discard Changes</b-btn>
    </div>
    </transition>
  </div>
</div>

</template>
<script>
import {add_yard, delete_yard, update_yard, get_tenant_config_settings, update_tenant_config_settings, get_users} from '../networking/apis'
import filterValidationMixin from '../mixins/filter_validation_mixin'
import dirtyTrackingMixin from '../mixins/dirty_tracking_mixin'
import jsonValidationMixin from '../mixins/json_validation_mixin'
import { copyToClipboard } from '@/helpers/vsm_standalone_url'
import {mapState} from "vuex";
import JsonObjectListEditor from './JsonObjectListEditor.vue'
import FilterSettingEditor from './FilterSettingEditor.vue'
import { ensureFilterKeys } from '../utils/filter_syntax'

export default {
  name: 'tenant_config',
  mixins: [filterValidationMixin, dirtyTrackingMixin, jsonValidationMixin],
  components: {
    JsonObjectListEditor,
    FilterSettingEditor,
  },

  data() {
    return {
      add_yard_model: {},
      editing_yard_name: null,
      manage_sos_yards: false,
      model: {},
      general_model: {},
      general_advanced_model: {},
      toggle: {},
      summary: {},
      loading: true,
      tenant_id_copied: false,
      tenant_id_copied_timer: null,
      yardsLength: 0,
      objectKeyList:[],
      jsonFieldToggleMap: {
        telematics_yard_overrides: 'isAdvVisible',
      },
      // select-backed flags that may be stored as real booleans by scripts
      booleanFlagKeys: ['coaching_portal_enabled'],
      boolean_options: [
        { text: 'True', value: 'True' },
        { text: 'False', value: 'False' },
      ],
      telematics_yard_overrides_columns: [
        { key: 'telematics_type', label: 'Provider', options: [
          { value: '', text: '(select)' },
          { value: 'SAMSARA', text: 'Samsara' },
          { value: 'ZONAR', text: 'Zonar' },
          { value: 'MOTIVE', text: 'Motive' },
        ], width: '110px' },
        { key: 'yard_names', label: 'Yards (comma-separated, must match FirstStartBase)', width: '260px' },
        { key: 'api_key', label: 'API Key', width: '160px' },
        { key: 'org_id', label: 'Org ID (Samsara)', width: '110px' },
        { key: 'group_id', label: 'Group ID (Samsara)', width: '110px' },
      ],
      motorcoach_organization_options: ['IMG', 'UMA', 'ABA', 'CBA', 'GNEMA'],
    }
  },
  async mounted() {
    this.load_data()
  },
  methods: {
    async delete_yard(yard_name) {
      this.loading=true
      await delete_yard(this.user.tenant, yard_name)
      await this.load_data()
    },
    edit_yard(yard) {
      this.editing_yard_name = yard.yard_name
      this.add_yard_model = { ...yard }
      this.update_toggle_div(this.toggle, 'isAddYardVisible', true)
    },
    cancel_edit_yard() {
      this.editing_yard_name = null
      this.add_yard_model = {}
      this.toggle_div(this.toggle, 'isAddYardVisible')
    },
    async save_yard() {
      this.loading=true
      if (this.editing_yard_name) {
        await update_yard(this.user.tenant, { ...this.add_yard_model, original_yard_name: this.editing_yard_name })
      } else {
        await add_yard(this.user.tenant, this.add_yard_model)
      }
      this.editing_yard_name = null
      this.add_yard_model = {}
      this.toggle_div(this.toggle, 'isAddYardVisible')
      await this.load_data()
    },
    async copy_tenant_id() {
      if (!this.user.tenant)
        return
      try {
        await copyToClipboard(String(this.user.tenant))
        this.tenant_id_copied = true
        if (this.tenant_id_copied_timer)
          clearTimeout(this.tenant_id_copied_timer)
        this.tenant_id_copied_timer = setTimeout(() => {
          this.tenant_id_copied = false
        }, 2000)
      } catch (e) {
        this.$emit('showAlert', 'unable to copy tenant ID', 'danger', 3000)
      }
    },
    async hosModalOk($event) {
      this.add_event_model = {}
      this.update_toggle_div(this.toggle, 'isAddEventVisible', false)
    },

    async on_settings_change_email_toggle(value) {
      if (value !== 'True' || this.model.settings_change_email_recipients)
        return
      try {
        const { value: { data = {} } } = await get_users(this.user.tenant)
        const admin_emails = (data.results || [])
          .filter(u => u.role === 'customer-admin')
          .map(u => u.username)
        if (admin_emails.length)
          this.$set(this.model, 'settings_change_email_recipients', admin_emails.join(','))
      } catch (e) {
        console.log('error fetching default settings-change email recipients', e)
      }
    },

    normalize_motorcoach_organizations() {
      const raw = this.model.motorcoach_organizations
      if (raw == null || raw === '' || raw === '[]') {
        this.$set(this.model, 'motorcoach_organizations', [])
        return
      }
      if (typeof raw === 'string') {
        try {
          this.$set(this.model, 'motorcoach_organizations', JSON.parse(raw))
        } catch (e) {
          this.$set(this.model, 'motorcoach_organizations', [])
        }
      }
      if (!Array.isArray(this.model.motorcoach_organizations))
        this.$set(this.model, 'motorcoach_organizations', [])
      this.objectKeyList = this.objectKeyList.filter(key => key !== 'motorcoach_organizations')
    },
    async load_data(silent = false) {
      if (!silent) this.loading = true
      this.objectKeyList = []
      let loaded = false
      try {
        const {value: { data = [] } } = await get_tenant_config_settings(this.user.tenant)
        this.summary = data.summary
        this.model = data.results
        ensureFilterKeys(this.model, [
          'samsara_tag_filter',
          'samsara_driver_tag_filter',
          'email_response_forward_filters',
          'allowed_email_recipients',
          'allowed_sms_recipients',
          'process_booking_ids',
        ])
        if (!this.model.vehicle_yards)
          this.model.vehicle_yards = []

        if (this.model.vehicle_yards == '[]')
          this.model.vehicle_yards = []
        
        try {
          const entries = Object.entries(this.model);
          for (let [key,value] of entries) {
            if (key === 'motorcoach_organizations' || key === 'vehicle_yards')
              continue
            if (this.isArrayType(value) || this.isPlainObject(value,key)) {
              let setting = JSON.stringify(value)
              //console.log('found plain object or array: key:'+key+ ' value: '+ setting);
              this.model[key] = setting
              this.objectKeyList.push(key)
            }
          }
        } catch(e) {
          console.log('error looping through keys',e)
        }
        try {
          if (this.model.vehicle_yards != "" && this.model.vehicle_yards != null) {
            this.model.vehicle_yards = JSON.parse(this.model.vehicle_yards);
          } else {
            this.model.vehicle_yards = []
          }
        } catch(e) {
          console.log('exception converting hos alert settings to json:' + e)
        }

        this.yardsLength = Array.isArray(this.model.vehicle_yards) ? this.model.vehicle_yards.length : 0
        this.normalize_motorcoach_organizations()
        this.normalize_boolean_flags()
        loaded = true
      } catch(e) {
        this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
      }

      this.loading = false
      if (loaded) this._resetDirtyTracking()
    },
    // flags set programmatically (provisioning scripts, seeders) land in the
    // tenant record as real booleans, while this page's selects offer the
    // strings 'True'/'False' — an unmatched value renders the dropdown blank,
    // so it reads as "off" when the feature is actually on. coerce on load,
    // before the dirty snapshot is taken, so the page doesn't read as dirty.
    normalize_boolean_flags() {
      for (const key of this.booleanFlagKeys) {
        if (typeof this.model[key] === 'boolean')
          this.$set(this.model, key, this.model[key] ? 'True' : 'False')
      }
    },
    async get_yard_length(model) {
      this.yardsLength = model.vehicle_yards.length
    },
    async save_config() {
      try {
        this.saving = true
        this.flushFilterValidation()
        if (this.hasFilterErrors()) {
          this.$emit('showAlert', 'Fix the highlighted filter field before saving', 'danger', 6000)
          this.scrollToFirstFilterError()
          return
        }
        if (this.hasJsonErrors()) {
          this.$emit('showAlert', 'Invalid JSON in field: ' + this.firstJsonErrorKey() + ' — ' + this.jsonFeedback(this.firstJsonErrorKey()), 'danger', 8000)
          this.expandAndScrollToJsonError()
          return
        }
        // Compute dirty keys before parsing (model still holds JSON strings matching snapshot).
        const dirtyKeys = new Set(Object.keys(this._dirtyFields()))
        if (dirtyKeys.size === 0) {
          this.$emit('showAlert', 'No changes to save', 'info', 3000)
          return
        }
        this.$emit('showAlert', 'Saving Changes', 'info',5000)
        const fullPayload = Object.assign({}, this.model)
        for (const objkey of this.objectKeyList) {
          try {
            if (fullPayload[objkey] && fullPayload[objkey].trim && fullPayload[objkey].trim() !== '') {
              fullPayload[objkey] = JSON.parse(fullPayload[objkey])
            }
          } catch (e) {
            this.$emit('showAlert', 'Invalid JSON in field: ' + objkey + ' — ' + e.message, 'danger', 8000)
            this.load_data()
            return
          }
        }
        const payload = {}
        for (const key of dirtyKeys) {
          if (key in fullPayload) payload[key] = fullPayload[key]
        }
        if ('motorcoach_organizations' in payload && (!payload.motorcoach_organizations || !payload.motorcoach_organizations.length))
          payload.motorcoach_organizations = null
        await update_tenant_config_settings(this.user.tenant, payload)
        this._resetDirtyTracking()
        await this.load_data(true)
        this.$emit('showAlert', 'Configuration Saved', 'success',5000)
      } catch(e) {
        this.$emit('showAlert', this._saveErrorMessage(e), 'danger', 5000)
      }
      finally {
        this.saving = false
      }
    },
    set_tenant_id(id) {
      this.user.tenant = id
      this.load_data()
    },
    update_toggle_div(item,field,setting) {
      if (!item[field] && setting) {
        item[field] = !item[field];
        this.$forceUpdate();
      } else if (item[field] && !setting) {
        item[field] = !item[field];
        this.$forceUpdate();
      }
    },
    toggle_div(item,field) {
      item[field] = !item[field];
      this.$forceUpdate();
    },
    isPlainObject(value,key) {
      //console.log('isPlainObject, value:' + value + ', type of value:' + (typeof value))
      let objtype = (typeof value)
      if (objtype == "object") {
        if (value != null && !Array.isArray(value)) {
          return true
        }
        return false
      }
    },
    isArrayType(value) {
      return Array.isArray(value)
    },
    isDevEnvironment() {
      return (process.env.VUE_APP_API_URL.includes('dev-web') || process.env.VUE_APP_API_URL.includes('localhost'))
    }
  } ,
  computed:{
    ...mapState({
      user: state => state.profile.user,
      viewAs: state => state.profile.view_as,
    }),
    isEffectiveAdmin() {
      return this.user.role === 'admin' && this.viewAs !== 'non-admin'
    },
    telematics_type() {
      return this.user.telematics_types[this.user.tenant]
    },
    tenant_type() {
      return this.user.types[this.user.tenant]
    }
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.load_data()
      }
    }
  }
}

</script>
<style>
@import "../../node_modules/@syncfusion/ej2-base/styles/bootstrap5.css";
@import "../../node_modules/@syncfusion/ej2-inputs/styles/bootstrap5.css";
@import "../../node_modules/@syncfusion/ej2-lists/styles/bootstrap5.css";
@import "../../node_modules/@syncfusion/ej2-popups/styles/bootstrap5.css";
@import "../../node_modules/@syncfusion/ej2-buttons/styles/bootstrap5.css";
@import "../../node_modules/@syncfusion/ej2-navigations/styles/bootstrap5.css";
@import "../../node_modules/@syncfusion/ej2-splitbuttons/styles/bootstrap5.css";
@import "../../node_modules/@syncfusion/ej2-richtexteditor/styles/bootstrap5.css";

.motorcoach-org-checkboxes >>> .custom-control {
  display: inline-block;
  margin-right: 1.25rem;
}
</style>

